import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react'
import { usePostLoginQuery } from '../../api/ester-b2b-api/private/users/userQueries'
import { isLoginErrorCode } from '../../utils/loginErrors'
import { useGetActiveServicesQuery } from '../../api/ester-b2b-api/private/services/serviceQueries'
import { useGetFeatureFlagsQuery } from '../../api/ester-b2b-api/private/featureFlags/featureFlagQueries'
import { useEffect, useState } from 'react'
import { LoadingSpinner } from '../LoadingSpinner'
import { theme } from '../../theme/theme'
import { useNavigate, Outlet } from 'react-router-dom'
import { useUserStore, useServicesStore, useFeatureFlagsStore } from '../../state/store'
import { Sidebar } from '../sidebar/Sidebar'
import { ViewLayout } from '../layouts/ViewLayout'
import { SupplementaryDataModal } from '../SupplementaryDataModal/SupplementaryDataModal'
import {
  useIsFeatureFlagsLoaded,
  useIsUserSet,
  useSelectDataPolicyAcknowledgementRequired,
  useSelectUserMarket,
} from '../../state/selectors'
import i18n from '../../i18n'
import { Services } from '@estercare/ester-shared'
import { isHealthCallsEnabled } from '../../utils/market'
import { DataPolicyStep } from '../../views/policy/DataPolicyStep'

const AuthenticationWrapper = () => {
  const { setUser } = useUserStore()
  const { setActiveServices } = useServicesStore()
  const { setFeatureFlags } = useFeatureFlagsStore()
  const { isAuthenticated, logout } = useAuth0()
  const navigate = useNavigate()
  const isUserFetched = useIsUserSet()
  const isFeatureFlagsLoaded = useIsFeatureFlagsLoaded()
  const userMarket = useSelectUserMarket()
  const acknowledgementRequired = useSelectDataPolicyAcknowledgementRequired()
  const [languageReady, setLanguageReady] = useState(false)
  const { mutate: logInUser } = usePostLoginQuery({
    options: {
      onError: (error) => {
        if (isLoginErrorCode(error.message)) {
          navigate('/error', { state: { errorMessage: error.message } })
          return
        }
        logout({
          openUrl() {
            navigate('/error', { state: { errorMessage: error.message } })
          },
        })
      },
      onSuccess: (user) => {
        setUser(user)
        const userProperties = {
          ...(user.organisationName && { organisation_name: user.organisationName }),
          ...(user.department && { department: user.department }),
        }
        if (Object.keys(userProperties).length > 0) {
          window.gtag?.('set', 'user_properties', userProperties)
        }
        if (user.language && i18n.language !== user.language) {
          i18n.changeLanguage(user.language).finally(() => {
            setLanguageReady(true)
          })
        } else if (!user.language) {
          // Check browser language if user hasn't selected one
          const browserLanguage = navigator.language || navigator.languages?.[0] || 'sv' // We default to Swedish for now if we fail getting browser language
          const languageToUse = browserLanguage.startsWith('sv') ? 'sv' : 'en'
          i18n.changeLanguage(languageToUse).finally(() => {
            setLanguageReady(true)
          })
        } else {
          setLanguageReady(true)
        }
      },
    },
  })
  const { data: servicesData, isLoading: isServicesLoading } = useGetActiveServicesQuery({
    enabled: isUserFetched && !acknowledgementRequired,
  })
  const { data: featureFlagsData, isLoading: isFeatureFlagsLoading } = useGetFeatureFlagsQuery({
    enabled: isUserFetched && !acknowledgementRequired,
  })

  useEffect(() => {
    if (isAuthenticated && !isUserFetched) {
      logInUser()
    }
  }, [isAuthenticated, logInUser, isUserFetched])

  useEffect(() => {
    if (servicesData && servicesData.length > 0) {
      // Filter out HEALTH_CALL if not enabled for user's market
      const filteredServices = servicesData.filter((service) => {
        if (service === Services.HEALTH_CALL) {
          return isHealthCallsEnabled(userMarket)
        }
        return true
      })
      setActiveServices(filteredServices)
    }
  }, [servicesData, setActiveServices, userMarket])

  useEffect(() => {
    if (featureFlagsData) {
      setFeatureFlags(featureFlagsData)
    }
  }, [featureFlagsData, setFeatureFlags])

  // The flags reach the store in the effect above, one commit after the request resolves. Routed pages that mount in
  // that gap would read every flag as off and fetch with it. A failed request has no data, so it never waits here.
  const isFeatureFlagsStorePending = !!featureFlagsData && !isFeatureFlagsLoaded

  if (isServicesLoading || isFeatureFlagsLoading || isFeatureFlagsStorePending || !isUserFetched || !languageReady) {
    return <LoadingSpinner size={75} color={theme.color.plum} />
  }

  if (acknowledgementRequired) {
    return <DataPolicyStep />
  }

  return (
    <>
      <Sidebar />
      <ViewLayout>
        <Outlet />
        <SupplementaryDataModal />
      </ViewLayout>
    </>
  )
}

export const AuthenticationGuard = withAuthenticationRequired(AuthenticationWrapper, {
  onRedirecting: () => <LoadingSpinner size={75} color={theme.color.plum} />,
})
